import { v4 } from "uuid";

import {
  DiscountTierDto,
  OfferQuantityButtonType,
  PartialUserInterfaceDto,
  UserInterfaceDto,
  WidgetTypeType,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import { toBuyMoreTemplateReplacements } from "../../features/personalizations/utils";
import { IUserInterfacesState } from "../types/userInterfaces";

const sliceInitialUserInterfacesState: IUserInterfacesState = {
  isSuccess: false,
  userInterfaces: [],
  selectedMessageBoxInterface: undefined,
  storedSelectedMessageBoxInterface: undefined,
  selectedProgressBarInterface: undefined,
  storedSelectedProgressBarInterface: undefined,
  selectedQuantityInterface: undefined,
  storedSelectedQuantityInterface: undefined,
};

const userInterfacesSlice = rtk.createSlice({
  name: "userInterfaces",
  initialState: sliceInitialUserInterfacesState,
  reducers: {
    resetUserInterfaces: (draft) => {
      draft = sliceInitialUserInterfacesState;
      return draft;
    },
    setUserInterfaces: (draft, action: PayloadAction<UserInterfaceDto[]>) => {
      draft.isSuccess = true;
      draft.userInterfaces = action.payload;
      return draft;
    },
    setQuantityInterface: (
      draft,
      action: PayloadAction<{
        interface: PartialUserInterfaceDto;
        isInit?: boolean;
      }>
    ) => {
      draft.selectedQuantityInterface = action.payload.interface;
      if (action.payload.isInit) {
        draft.storedSelectedQuantityInterface = action.payload.interface;
      }
      return draft;
    },
    setProgressBarInterface: (
      draft,
      action: PayloadAction<{
        interface: PartialUserInterfaceDto;
        isInit?: boolean;
      }>
    ) => {
      draft.selectedProgressBarInterface = action.payload.interface;
      if (action.payload.isInit) {
        draft.storedSelectedProgressBarInterface = action.payload.interface;
      }
      return draft;
    },
    setMessageBoxInterface: (
      draft,
      action: PayloadAction<{
        interface: PartialUserInterfaceDto;
        isInit?: boolean;
      }>
    ) => {
      draft.selectedMessageBoxInterface = action.payload.interface;
      if (action.payload.isInit) {
        draft.storedSelectedMessageBoxInterface = action.payload.interface;
      }
      return draft;
    },
    setQuantityInterfaceFieldChange: (
      draft,
      action: PayloadAction<{
        key: keyof OfferQuantityButtonType;
        value: string;
        buttonIndex: number;
      }>
    ) => {
      if (draft.selectedQuantityInterface) {
        if (action.payload.key === "quantity") {
          draft.selectedQuantityInterface.desktopVariables.buttons[
            action.payload.buttonIndex
          ].quantity = parseFloat(action.payload.value);
        } else {
          draft.selectedQuantityInterface.desktopVariables.buttons[
            action.payload.buttonIndex
          ][action.payload.key] = action.payload.value;
        }
      }
      return draft;
    },
    setQuantityInterfaceDeleteSelector: (
      draft,
      action: PayloadAction<{
        buttonIndex: number;
      }>
    ) => {
      if (draft.selectedQuantityInterface) {
        draft.selectedQuantityInterface.desktopVariables.buttons.splice(
          action.payload.buttonIndex,
          1
        );
      }
      return draft;
    },
    setQuantityInterfaceAddSelector: (draft) => {
      if (draft.selectedQuantityInterface) {
        if (
          draft.selectedQuantityInterface.desktopVariables.buttons.length > 0
        ) {
          draft.selectedQuantityInterface.desktopVariables.buttons.push({
            id: v4(),
            quantity:
              draft.selectedQuantityInterface.desktopVariables.buttons[
                draft.selectedQuantityInterface.desktopVariables.buttons
                  .length - 1
              ].quantity + 1,
            unitName:
              draft.selectedQuantityInterface.desktopVariables.buttons[
                draft.selectedQuantityInterface.desktopVariables.buttons
                  .length - 1
              ].unitName,
          });
        } else {
          draft.selectedQuantityInterface.desktopVariables.buttons.push({
            id: v4(),
            quantity: 1,
            unitName: "unit",
          });
        }
      }
      return draft;
    },
    setInterfaceTextReplacements: (
      draft,
      action: PayloadAction<{
        widgetType?: WidgetTypeType;
        discountTier?: DiscountTierDto;
        templateKey: string;
        value: string;
      }>
    ) => {
      if (
        action.payload?.widgetType === "discountProgressBar" &&
        action.payload.discountTier
      ) {
        if (draft.selectedProgressBarInterface) {
          draft.selectedProgressBarInterface.desktopVariables.textReplacements.tierVariables =
            {
              ...draft.selectedProgressBarInterface.desktopVariables
                .textReplacements?.tierVariables,
              [action.payload.discountTier.id]: {
                ...draft.selectedProgressBarInterface.desktopVariables
                  .textReplacements?.tierVariables?.[
                  action.payload.discountTier.id
                ],
                [action.payload.templateKey]: toBuyMoreTemplateReplacements(
                  action.payload.value
                ),
              },
            };
        }
      } else {
        if (draft.selectedMessageBoxInterface) {
          draft.selectedMessageBoxInterface.desktopVariables.textReplacements[
            action.payload.templateKey
          ] = toBuyMoreTemplateReplacements(action.payload.value);
        }
      }
      return draft;
    },
    setProgressBarInterfaceKeyChange: (
      draft,
      action: PayloadAction<{
        value: string;
        key: "more" | "unitName";
      }>
    ) => {
      if (draft.selectedProgressBarInterface) {
        draft.selectedProgressBarInterface.desktopVariables.textReplacements[
          action.payload.key
        ] = action.payload.value;
      }
      return draft;
    },
  },
  selectors: {
    selectedUserInterfaces: (state) => state.userInterfaces,
    selectedQuantityInterface: (state) => state.selectedQuantityInterface,
    selectedProgressBarInterface: (state) => state.selectedProgressBarInterface,
    selectedMessageBoxInterface: (state) => state.selectedMessageBoxInterface,
    selectedUserInterfaceState: (state) => state,
  },
});

export const {
  resetUserInterfaces,
  setQuantityInterface,
  setQuantityInterfaceFieldChange,
  setQuantityInterfaceDeleteSelector,
  setQuantityInterfaceAddSelector,
  setUserInterfaces,
  setProgressBarInterface,
  setMessageBoxInterface,
  setInterfaceTextReplacements,
  setProgressBarInterfaceKeyChange,
} = userInterfacesSlice.actions;

export const userInterfacesReducer = userInterfacesSlice.reducer;
export const {
  selectedUserInterfaces,
  selectedQuantityInterface,
  selectedProgressBarInterface,
  selectedMessageBoxInterface,
  selectedUserInterfaceState,
} = userInterfacesSlice.selectors;
